import {
  type ICategoryEbay,
  type IEbayAspect,
  type IEbayFulfillment,
  type IEbayMarketplace,
  type IEbayPayment,
  type IEbayReturn,
  type IProductEbayDetail,
} from '@mahawi/eshop-common/src/types';
import {
  type IEbayBotGetInventoryItemResponse,
  type IEbayBotGetOffersResponse,
  type IEbayBotPostOfferResponse,
  type IEbayBotPublishOfferResponse,
  type IEbayBotPutInventoryItemResponse,
  type IEbayBotPutOfferResponse,
  type IEbayCategoryNode,
} from '@mahawi/eshop-rabbit';
import { createSlice } from '@reduxjs/toolkit';

import {
  requestFailedMatcher,
  resetLoadingOnRequestFailed,
} from '../async-state';
import { type IEbayState, type TEbayAspectsValues } from './types';

export const preloadedState: IEbayState = {
  aspects: {},
  categories: {},
  categorySuggestions: {},
  fulfillmentPolicies: {},
  inProcess: false,
  inventoryItems: {},
  marketplace: undefined,
  marketplaces: [],
  marketplacesTimestamp: 0,
  offers: {},
  paymentPolicies: {},
  productEbay: {},
  returnPolicies: {},
  userUrl: undefined,
};

const ebaySlice = createSlice({
  name: 'Ebay',
  initialState: preloadedState,
  reducers: {
    ebayGetUserUrl: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare() {
        return { payload: undefined };
      },
    },
    ebayGetUserUrlResponse: {
      reducer(state, action: { payload: { url: string } }) {
        state.inProcess = false;
        state.userUrl = action.payload.url;
      },
      prepare(url: string) {
        return { payload: { url } };
      },
    },
    ebayUserCode: {
      reducer() {},
      prepare(code: string) {
        return { payload: { code } };
      },
    },
    ebayMarketplacesLoad: {
      reducer(state) {
        state.inProcess = true;
        state.marketplaces = [];
        state.marketplacesTimestamp = Date.now();
        state.offers = {};
        state.inventoryItems = {};
      },
      prepare() {
        return { payload: undefined };
      },
    },
    ebayMarketplacesLoadResponse: {
      reducer(
        state,
        action: { payload: { marketplaces: IEbayMarketplace[] } },
      ) {
        state.inProcess = false;
        state.marketplaces = action.payload.marketplaces;
        state.marketplacesTimestamp = Date.now();
        state.offers = {};
        state.inventoryItems = {};
      },
      prepare(marketplaces: IEbayMarketplace[]) {
        return { payload: { marketplaces } };
      },
    },
    ebayMarketplaceLoad: {
      reducer(state) {
        state.inProcess = true;
        state.marketplace = undefined;
      },
      prepare(code: string) {
        return { payload: { ebayMarketplace: { code } } };
      },
    },
    ebayMarketplaceLoadResponse: {
      reducer(
        state,
        action: { payload: { marketplace: IEbayMarketplace | null } },
      ) {
        state.inProcess = false;
        state.marketplace = action.payload.marketplace;
      },
      prepare(marketplace: IEbayMarketplace | null) {
        return { payload: { marketplace } };
      },
    },
    ebayMarketplaceLoadFulfillmentPolicies: {
      reducer() {},
      prepare(code: string) {
        return { payload: { ebayMarketplace: { code } } };
      },
    },
    ebayMarketplaceLoadFulfillmentPoliciesResponse: {
      reducer(
        state,
        action: {
          payload: {
            ebayMarketplace: { code: string };
            fulfillmentPolicies?: IEbayFulfillment[];
          };
        },
      ) {
        if (action.payload.fulfillmentPolicies) {
          state.fulfillmentPolicies[action.payload.ebayMarketplace.code] =
            action.payload.fulfillmentPolicies;
        }
      },
      prepare(code: string, fulfillmentPolicies?: IEbayFulfillment[]) {
        return { payload: { ebayMarketplace: { code }, fulfillmentPolicies } };
      },
    },
    ebayMarketplaceLoadPaymentPolicies: {
      reducer() {},
      prepare(code: string) {
        return { payload: { ebayMarketplace: { code } } };
      },
    },
    ebayMarketplaceLoadPaymentPoliciesResponse: {
      reducer(
        state,
        action: {
          payload: {
            ebayMarketplace: { code: string };
            paymentPolicies: IEbayPayment[];
          };
        },
      ) {
        state.paymentPolicies[action.payload.ebayMarketplace.code] =
          action.payload.paymentPolicies;
      },
      prepare(code: string, paymentPolicies: IEbayPayment[]) {
        return { payload: { ebayMarketplace: { code }, paymentPolicies } };
      },
    },
    ebayMarketplaceLoadReturnPolicies: {
      reducer() {},
      prepare(code: string) {
        return { payload: { ebayMarketplace: { code } } };
      },
    },
    ebayMarketplaceLoadReturnPoliciesResponse: {
      reducer(
        state,
        action: {
          payload: {
            ebayMarketplace: { code: string };
            returnPolicies: IEbayReturn[];
          };
        },
      ) {
        state.returnPolicies[action.payload.ebayMarketplace.code] =
          action.payload.returnPolicies;
      },
      prepare(code: string, returnPolicies: IEbayReturn[]) {
        return { payload: { ebayMarketplace: { code }, returnPolicies } };
      },
    },
    ebayMarketplaceSetIsActive: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(code: string, isActive: boolean) {
        return { payload: { ebayMarketplace: { code }, isActive } };
      },
    },
    ebayMarketplaceSetIsActiveResponse: {
      reducer(
        state,
        action: { payload: { marketplace: IEbayMarketplace | null } },
      ) {
        state.inProcess = false;
        state.marketplace = action.payload.marketplace;
      },
      prepare(marketplace: IEbayMarketplace | null) {
        return { payload: { marketplace } };
      },
    },
    ebayMarketplaceSetLanguage: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(code: string, languageCode: string) {
        return {
          payload: {
            ebayMarketplace: { code },
            languageType: { code: languageCode },
          },
        };
      },
    },
    ebayMarketplaceSetLanguageResponse: {
      reducer(
        state,
        action: { payload: { marketplace: IEbayMarketplace | null } },
      ) {
        state.inProcess = false;
        state.marketplace = action.payload.marketplace;
      },
      prepare(marketplace: IEbayMarketplace | null) {
        return { payload: { marketplace } };
      },
    },
    ebayMarketplaceSetCurrency: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(code: string, currencyIsoCode: string) {
        return {
          payload: {
            ebayMarketplace: { code },
            currencyType: { isoCode: currencyIsoCode },
          },
        };
      },
    },
    ebayMarketplaceSetCurrencyResponse: {
      reducer(
        state,
        action: { payload: { marketplace: IEbayMarketplace | null } },
      ) {
        state.inProcess = false;
        state.marketplace = action.payload.marketplace;
      },
      prepare(marketplace: IEbayMarketplace | null) {
        return { payload: { marketplace } };
      },
    },
    ebayMarketplacReloadDefaultCategoryId: {
      reducer() {},
      prepare(code: string) {
        return { payload: { ebayMarketplace: { code } } };
      },
    },
    ebayMarketplacReloadFulfillmentPolicies: {
      reducer() {},
      prepare(code: string) {
        return { payload: { ebayMarketplace: { code } } };
      },
    },
    ebayMarketplacReloadPaymentPolicies: {
      reducer() {},
      prepare(code: string) {
        return { payload: { ebayMarketplace: { code } } };
      },
    },
    ebayMarketplacReloadReturnPolicies: {
      reducer() {},
      prepare(code: string) {
        return { payload: { ebayMarketplace: { code } } };
      },
    },
    ebayMarketplacReloadCategories: {
      reducer() {},
      prepare(code: string, categoryId: string) {
        return { payload: { ebayMarketplace: { code }, categoryId } };
      },
    },
    ebayCategorySuggestions: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(code: string, query: string) {
        return { payload: { ebayMarketplace: { code }, query } };
      },
    },
    ebayCategorySuggestionsResponse: {
      reducer(
        state,
        action: {
          payload: {
            ebayMarketplace: { code: string };
            categorySuggestions?: IEbayCategoryNode[] | null;
          };
        },
      ) {
        state.inProcess = false;
        if (action.payload.categorySuggestions) {
          state.categorySuggestions[action.payload.ebayMarketplace.code] =
            action.payload.categorySuggestions;
        }
      },
      prepare(code: string, categorySuggestions?: IEbayCategoryNode[] | null) {
        return { payload: { ebayMarketplace: { code }, categorySuggestions } };
      },
    },
    ebayCategoryLoad: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(categoryUUID: string, code: string) {
        return {
          payload: {
            category: { uuid: categoryUUID },
            ebayMarketplace: { code },
          },
        };
      },
    },
    ebayCategoryLoadResponse: {
      reducer(
        state,
        action: {
          payload: {
            ebayMarketplace: { code: string };
            category: { uuid: string };
            categories?: ICategoryEbay[] | null;
          };
        },
      ) {
        state.inProcess = false;
        const { code } = action.payload.ebayMarketplace;
        const { uuid } = action.payload.category;
        if (!state.categories[code]) {
          state.categories[code] = {};
        }
        if (action.payload.categories) {
          state.categories[code][uuid] = action.payload.categories;
        }
      },
      prepare(
        categoryUUID: string,
        code: string,
        categories?: ICategoryEbay[] | null,
      ) {
        return {
          payload: {
            category: { uuid: categoryUUID },
            ebayMarketplace: { code },
            categories,
          },
        };
      },
    },
    ebayCategorySave: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(categoryUUID: string, code: string, categoryId: string) {
        return {
          payload: {
            category: { uuid: categoryUUID },
            ebayMarketplace: { code },
            categoryId,
          },
        };
      },
    },
    ebayCategorySaveAspects: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(productUUID: string, code: string, values: TEbayAspectsValues) {
        return {
          payload: {
            product: { uuid: productUUID },
            ebayMarketplace: { code },
            values,
          },
        };
      },
    },
    ebayCategorySaveAspectsResponse: {
      reducer(state) {
        state.inProcess = false;
      },
      prepare() {
        return { payload: undefined };
      },
    },
    ebayCaregoryLoadAspects: {
      reducer(state) {
        state.inProcess = true;
        state.aspects = {};
      },
      prepare(productUUID: string, code: string) {
        return {
          payload: {
            product: { uuid: productUUID },
            ebayMarketplace: { code },
          },
        };
      },
    },
    ebayCaregoryLoadAspectsResponse: {
      reducer(
        state,
        action: {
          payload: {
            ebayMarketplace: { code: string };
            ebayAspects: IEbayAspect[];
          };
        },
      ) {
        state.inProcess = false;
        state.aspects[action.payload.ebayMarketplace.code] =
          action.payload.ebayAspects;
      },
      prepare(code: string, ebayAspects: IEbayAspect[]) {
        return { payload: { ebayMarketplace: { code }, ebayAspects } };
      },
    },
    ebayProductSave: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(
        productUUID: string,
        code: string,
        paymentPolicyId: string,
        fulfillmentPolicyId: string,
        returnPolicyId: string,
        inStock: number,
        priceIncreaseRate: number,
      ) {
        return {
          payload: {
            product: { uuid: productUUID },
            ebayMarketplace: { code },
            paymentPolicy: { id: paymentPolicyId },
            fulfillmentPolicy: { id: fulfillmentPolicyId },
            returnPolicy: { id: returnPolicyId },
            inStock,
            priceIncreaseRate,
          },
        };
      },
    },
    ebayProductSaveResponse: {
      reducer(
        state,
        action: {
          payload: {
            productEbay: IProductEbayDetail | null;
            ebayMarketplace: { code: string };
          };
        },
      ) {
        state.inProcess = false;
        state.productEbay[action.payload.ebayMarketplace.code] =
          action.payload.productEbay;
      },
      prepare(productEbay: IProductEbayDetail | null, code: string) {
        return { payload: { productEbay, ebayMarketplace: { code } } };
      },
    },
    ebayProductLoad: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(productUUID: string, code: string) {
        return {
          payload: {
            product: { uuid: productUUID },
            ebayMarketplace: { code },
          },
        };
      },
    },
    ebayProductLoadResponse: {
      reducer(
        state,
        action: {
          payload: {
            productEbay: IProductEbayDetail | null;
            ebayMarketplace: { code: string };
          };
        },
      ) {
        state.inProcess = false;
        state.productEbay[action.payload.ebayMarketplace.code] =
          action.payload.productEbay;
      },
      prepare(productEbay: IProductEbayDetail | null, code: string) {
        return { payload: { productEbay, ebayMarketplace: { code } } };
      },
    },
    ebayInventoryItemDelete: {
      reducer() {},
      prepare(productMPN: string, code: string) {
        return {
          payload: { product: { mpn: productMPN }, ebayMarketplace: { code } },
        };
      },
    },
    ebayInventoryItemDeleteResponse: {
      reducer() {},
      prepare(mpn: string, code: string, response?: boolean | null) {
        return {
          payload: { product: { mpn }, ebayMarketplace: { code }, response },
        };
      },
    },
    ebayInventoryItemLoad: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(productMPN: string, code: string) {
        return {
          payload: { product: { mpn: productMPN }, ebayMarketplace: { code } },
        };
      },
    },
    ebayInventoryItemLoadResponse: {
      reducer(
        state,
        action: {
          payload: {
            ebayMarketplace: { code: string };
            response?: IEbayBotGetInventoryItemResponse;
          };
        },
      ) {
        state.inProcess = false;
        if (action.payload.response) {
          state.inventoryItems[action.payload.ebayMarketplace.code] =
            action.payload.response;
        }
      },
      prepare(code: string, response?: IEbayBotGetInventoryItemResponse) {
        return { payload: { ebayMarketplace: { code }, response } };
      },
    },
    ebayInventoryItemPut: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(productUUID: string, code: string) {
        return {
          payload: {
            product: { uuid: productUUID },
            ebayMarketplace: { code },
          },
        };
      },
    },
    ebayInventoryItemPutResponse: {
      reducer(
        state,
        action: {
          payload: {
            ebayMarketplace: { code: string };
            response?: IEbayBotPutInventoryItemResponse | null;
          };
        },
      ) {
        state.inProcess = false;
        if (action.payload.response) {
          state.inventoryItems[action.payload.ebayMarketplace.code] =
            action.payload.response;
        }
      },
      prepare(
        code: string,
        response?: IEbayBotPutInventoryItemResponse | null,
      ) {
        return { payload: { ebayMarketplace: { code }, response } };
      },
    },
    ebayInventoryItemsClearAllResponses: {
      reducer(state) {
        state.inventoryItems = {};
      },
      prepare() {
        return { payload: undefined };
      },
    },
    ebayOffersLoad: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(productUUID: string, productMPN: string, code: string) {
        return {
          payload: {
            product: { mpn: productMPN, uuid: productUUID },
            ebayMarketplace: { code },
          },
        };
      },
    },
    ebayOffersLoadResponse: {
      reducer(
        state,
        action: {
          payload: {
            ebayMarketplace: { code: string };
            response?: IEbayBotGetOffersResponse | null;
          };
        },
      ) {
        state.inProcess = false;
        if (action.payload.response) {
          state.offers[action.payload.ebayMarketplace.code] =
            action.payload.response;
        }
      },
      prepare(code: string, response?: IEbayBotGetOffersResponse | null) {
        return { payload: { ebayMarketplace: { code }, response } };
      },
    },
    ebayOfferDelete: {
      reducer() {},
      prepare(ebayMarketplaceCode: string, offerId: string) {
        return {
          payload: { ebayMarketplace: { code: ebayMarketplaceCode }, offerId },
        };
      },
    },
    ebayOfferDeleteResponse: {
      reducer() {},
      prepare(code: string, offerId: string, response: boolean) {
        return { payload: { ebayMarketplace: { code }, offerId, response } };
      },
    },
    ebayOfferPost: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(productUUID: string, code: string) {
        return {
          payload: {
            product: { uuid: productUUID },
            ebayMarketplace: { code },
          },
        };
      },
    },
    ebayOfferPostResponse: {
      reducer(
        state,
        action: {
          payload: {
            ebayMarketplace: { code: string };
            response: IEbayBotPostOfferResponse | null;
          };
        },
      ) {
        state.inProcess = false;
        if (action.payload.response) {
          state.offers[action.payload.ebayMarketplace.code] =
            action.payload.response;
        }
      },
      prepare(code: string, response: IEbayBotPostOfferResponse | null) {
        return { payload: { ebayMarketplace: { code }, response } };
      },
    },
    ebayOfferPublish: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(productMPN: string, code: string) {
        return {
          payload: { product: { mpn: productMPN }, ebayMarketplace: { code } },
        };
      },
    },
    ebayOfferPublishResponse: {
      reducer(
        state,
        action: {
          payload: {
            ebayMarketplace: { code: string };
            response: IEbayBotPublishOfferResponse | null;
          };
        },
      ) {
        state.inProcess = false;
        if (action.payload.response) {
          state.offers[action.payload.ebayMarketplace.code] =
            action.payload.response;
        }
      },
      prepare(code: string, response: IEbayBotPublishOfferResponse | null) {
        return { payload: { ebayMarketplace: { code }, response } };
      },
    },
    ebayOfferPut: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(productMPN: string, code: string) {
        return {
          payload: { product: { mpn: productMPN }, ebayMarketplace: { code } },
        };
      },
    },
    ebayOfferPutResponse: {
      reducer(
        state,
        action: {
          payload: {
            ebayMarketplace: { code: string };
            response: IEbayBotPutOfferResponse | null;
          };
        },
      ) {
        state.inProcess = false;
        if (action.payload.response) {
          state.offers[action.payload.ebayMarketplace.code] =
            action.payload.response;
        }
      },
      prepare(code: string, response: IEbayBotPutOfferResponse | null) {
        return { payload: { ebayMarketplace: { code }, response } };
      },
    },
    ebayOfferClearAllResponses: {
      reducer(state) {
        state.offers = {};
      },
      prepare() {
        return { payload: undefined };
      },
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      requestFailedMatcher('Ebay'),
      resetLoadingOnRequestFailed,
    );
  },
});

export const {
  ebayCaregoryLoadAspects,
  ebayCaregoryLoadAspectsResponse,
  ebayCategoryLoad,
  ebayCategoryLoadResponse,
  ebayCategorySave,
  ebayCategorySaveAspects,
  ebayCategorySaveAspectsResponse,
  ebayCategorySuggestions,
  ebayCategorySuggestionsResponse,
  ebayGetUserUrl,
  ebayGetUserUrlResponse,
  ebayInventoryItemDelete,
  ebayInventoryItemDeleteResponse,
  ebayInventoryItemLoad,
  ebayInventoryItemLoadResponse,
  ebayInventoryItemPut,
  ebayInventoryItemPutResponse,
  ebayInventoryItemsClearAllResponses,
  ebayMarketplacReloadCategories,
  ebayMarketplacReloadDefaultCategoryId,
  ebayMarketplacReloadFulfillmentPolicies,
  ebayMarketplacReloadPaymentPolicies,
  ebayMarketplacReloadReturnPolicies,
  ebayMarketplaceLoad,
  ebayMarketplaceLoadFulfillmentPolicies,
  ebayMarketplaceLoadFulfillmentPoliciesResponse,
  ebayMarketplaceLoadPaymentPolicies,
  ebayMarketplaceLoadPaymentPoliciesResponse,
  ebayMarketplaceLoadResponse,
  ebayMarketplaceLoadReturnPolicies,
  ebayMarketplaceLoadReturnPoliciesResponse,
  ebayMarketplaceSetCurrency,
  ebayMarketplaceSetCurrencyResponse,
  ebayMarketplaceSetIsActive,
  ebayMarketplaceSetIsActiveResponse,
  ebayMarketplaceSetLanguage,
  ebayMarketplaceSetLanguageResponse,
  ebayMarketplacesLoad,
  ebayMarketplacesLoadResponse,
  ebayOfferClearAllResponses,
  ebayOfferDelete,
  ebayOfferDeleteResponse,
  ebayOfferPost,
  ebayOfferPostResponse,
  ebayOfferPublish,
  ebayOfferPublishResponse,
  ebayOfferPut,
  ebayOfferPutResponse,
  ebayOffersLoad,
  ebayOffersLoadResponse,
  ebayProductLoad,
  ebayProductLoadResponse,
  ebayProductSave,
  ebayProductSaveResponse,
  ebayUserCode,
} = ebaySlice.actions;

export default ebaySlice.reducer;
